import { useEffect, useRef, useState } from 'react';

interface UseActiveAnchorOptions {
  anchorIds: string[];
  defaultHref: string;
  stickyOffset: number;
}

export const useActiveAnchor = (contentRef: React.RefObject<HTMLDivElement | null>, options: UseActiveAnchorOptions) => {
  const { anchorIds, defaultHref, stickyOffset } = options;
  const [activeHref, setActiveHref] = useState(defaultHref);
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const sectionElements = anchorIds.map((id) => container.querySelector<HTMLElement>(`#${id}`)).filter(Boolean) as HTMLElement[];
    if (sectionElements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < suppressUntilRef.current) return;
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveHref(`#${visible[0].target.id}`);
        }
      },
      {
        rootMargin: `-${stickyOffset + 16}px 0px -40% 0px`,
        threshold: 0
      }
    );

    sectionElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [contentRef, anchorIds, stickyOffset]);

  const handleAnchorClick = (href: string) => {
    setActiveHref(href);
    suppressUntilRef.current = Date.now() + 800;
    const id = href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return { activeHref, handleAnchorClick };
};
